import { isNumber } from 'lodash';
import { asyncSimpleModal, getImgInfo } from '../../../lib/common';
import { IValidateUploadImageProps } from './types';
import { IMAGE_ERROR_MESSAGE } from '../../../constants/errorResponse/image';
import { DEFAULT_IMAGE_UPLOAD_FAIL_MESSAGE, MAX_IMAGE_SIZE } from './constants';

export const isEnableGifValid = (file: File, enableGif: boolean) => {
  if (file.name.toLowerCase().endsWith('gif') && /(image\/gif)$/i.test(file.type)) {
    return enableGif;
  }
  return true;
};

export const isFileTypeValid = (file: File, enableGif: boolean) => {
  const allowedImageExtensions = getAllowedImageExtensions(enableGif);

  const pattern = new RegExp(`^(image/(${allowedImageExtensions.join('|')}))$`, 'i');

  return (
    pattern.test(file.type) && allowedImageExtensions.some((extension) => file.name.toLowerCase().endsWith(extension))
  );
};

export const fileSizeValid = ({
  file,
  capacityLimit,
  cropMode,
}: {
  file: File;
  capacityLimit?: number;
  cropMode: boolean;
}) => {
  // file 사이즈 체크 1MB 이하만 받기
  let limitSize = 1024 * 1024;
  let limitText = '1MB';
  // 일반 이미지는 20mb 이하만 받기
  if (capacityLimit && isNumber(capacityLimit)) {
    limitSize = 1024 * 1024 * capacityLimit;
    limitText = `${capacityLimit}MB`;
  } else if (!cropMode) {
    limitSize = 1024 * 1024 * 20;
    limitText = '20MB';
  }

  return {
    isValid: file.size <= limitSize,
    limitText,
  };
};

export const handleError = async (message: string) => {
  await asyncSimpleModal({
    type: 'error',
    title: '이미지 업로드 실패',
    message,
  });
};
export const validateUploadImage = async (props: IValidateUploadImageProps) => {
  const { isUpdateImage, files, attachmentList, maxCnt, enableGif, capacityLimit, cropMode, restriction } = props;

  try {
    // 파일 존재 체크
    if (!files.length) {
      throw new Error(IMAGE_ERROR_MESSAGE.UPLOADIMAGE001);
    }

    // 상세 이미지 갯수 체크
    if (!isUpdateImage && attachmentList.length + files.length > maxCnt) {
      throw new Error(
        `업로드하려는 파일 수가 너무 많습니다.\n ${maxCnt - attachmentList.length}개 이하로 업로드 해주세요.`,
      );
    }

    for (const file of files) {
      // 파일 확장자 체크
      if (!isFileTypeValid(file, enableGif)) {
        const allowedImageExtensions = getAllowedImageExtensions(enableGif);
        throw new Error(`${allowedImageExtensions.join(', ')} 이미지만 업로드 가능합니다.`);
      }

      const { isValid, limitText } = fileSizeValid({ file, capacityLimit, cropMode });
      if (!isValid) {
        throw new Error(`이미지용량은 ${limitText}미만으로 제한합니다.`);
      }

      const imgInfo = await getImgInfo(file);
      /**
       * 사이즈(너비, 높이) 검사
       */
      if (imgInfo.width >= MAX_IMAGE_SIZE || imgInfo.height >= MAX_IMAGE_SIZE) {
        throw new Error(`10,000 픽셀 이상의 이미지는 등록이 불가합니다.`);
      }
      if (restriction) {
        const { widthMin, heightMin, widthMax, heightMax, width, height, square = false } = restriction;

        if (square && imgInfo.width / imgInfo.height !== 1) {
          throw new Error(`정방형 이미지만 업로드가 가능합니다.`);
        }

        // 너비(최소)의 제한이 있을경우
        if (widthMin && widthMin > imgInfo.width) {
          throw new Error(`너비 ${widthMin} 픽셀 이하의 이미지는 AMONDZ 에서 제대로 노출되지 않을 수 있습니다.`);
        }

        // 너비(최대)의 제한이 있을경우
        else if (widthMax && widthMax < imgInfo.width) {
          throw new Error(`너비 ${widthMax} 픽셀 이상의 이미지는 AMONDZ 에서 제대로 노출되지 않을 수 있습니다.`);
        }

        // 높이(최소)의 제한이 있을경우
        else if (heightMin && heightMin > imgInfo.height) {
          throw new Error(`높이 ${heightMin} 픽셀 이하의 이미지는 AMONDZ 에서 제대로 노출되지 않을 수 있습니다.`);
        }

        // 높이(최대)의 제한이 있을경우
        else if (heightMax && heightMax < imgInfo.height) {
          throw new Error(
            `높이 ${heightMax} 픽셀 이상의 이미지는 AMONDZ 에서 제대로 노출되지 않을 수 있습니다. 최대 ${heightMax} 픽셀 기준으로 이미지를 나눠서 올려주세요.`,
          );
        }

        const isNotExactWidth = width && width !== imgInfo.width;
        const isNotExactHeight = height && height !== imgInfo.height;

        /**
         *  정확한 너비, 높이가 맞지 않았을 경우
         */
        if (isNotExactWidth && isNotExactHeight) {
          throw new Error(`${width}*${height} 사이즈만 등록 가능합니다.`);
        }
      }
    }
  } catch (e: any) {
    const message = e.message || DEFAULT_IMAGE_UPLOAD_FAIL_MESSAGE;
    throw new Error(message);
  }
};

export const shouldSquareImage = async (file: File) => {
  try {
    const imgInfo = await getImgInfo(file);
    return imgInfo.width === imgInfo.height;
  } catch (e) {
    throw new Error(DEFAULT_IMAGE_UPLOAD_FAIL_MESSAGE);
  }
};

export const getAllowedImageExtensions = (enableGif: boolean) => {
  const allowedImageExtensions = ['jpg', 'jpeg', 'png'];

  if (enableGif) {
    allowedImageExtensions.push('gif');
  }

  return allowedImageExtensions;
};
